import React from "react";

import { TypeOfContribution } from "EmployerApplication/Components/FinalFormQuestions";
import BoltIcon from "Intuit/BoltIcon";
import HarmonyIcon from "Intuit/HarmonyIcon";
import isIntuit from "Intuit/isIntuit";
import _ from "lodash";
import { useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { validateRequired, validateZero, validateHundred, validateNumber, composeValidators } from "Utils/Validations";

import Button from "Cobalt/Button";
import { Table, TR, TD, SnugTR } from "Cobalt/TableV2";
import { TIERED_CONTRIBUTION_ROLES, CONTRIBUTION_TYPES, MIN_TIERED_CONTRIBUTIONS } from "Common/Constants";
import { SIFinalFormFieldNoCol } from "Common/forms";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";
import RemoveIcon from "Common/RemoveIcon";
import { Input } from "Theme/Input";
import { Select } from "Theme/Select";

import makeBem from "Utils/makeBem";

const bem = makeBem("TieredContributionsForm");

const TieredContributionsFormDumb = ({ multipleMedicalPlansOffered, contributionType }) => {
  const MAX_TIERED_CONTRIBUTIONS = Object.keys(TIERED_CONTRIBUTION_ROLES).length;

  const formValues = useFormState().values;

  const tierOptions = () => {
    const tieredContributionsFormValues = formValues.tiered_contributions;
    const tierOptions = _.invert(TIERED_CONTRIBUTION_ROLES);
    let selectedRoles = [];

    if (tieredContributionsFormValues !== undefined) {
      selectedRoles = tieredContributionsFormValues.map((contribution) => contribution.role);
    }

    const arrayOfTierOptions = selectedRoles.map((selectedRoleForRow) => {
      const tierOptionsAsArray = Object.entries(tierOptions);
      // remove all roles that have been selected on other rows - leaving in the role selected on the current row
      const filteredTierOptions = tierOptionsAsArray.filter(
        ([_roleDisplay, roleValue]) => roleValue === selectedRoleForRow || !selectedRoles.includes(roleValue),
      );

      return _.fromPairs(filteredTierOptions);
    });

    return arrayOfTierOptions;
  };

  const percentAmountHeaders = () => (
    <>
      <TD align="right">
        <span className="max-lg:hidden">Employee Percentage </span>
        <span className="max-md:hidden lg:hidden">Employee Percentage </span>
        <span className="max-sm:hidden md:hidden">Employee %</span>
        <span className="sm:hidden">Emp % </span>{" "}
        <InfoPopover
          infoText="The percentage of monthly premiums the business will cover"
          displayComponent={
            <span>
              <Icon className={bem("tooltipIcon")} icon="info-circle" flavor="solid" />
            </span>
          }
          placement="top"
        />
      </TD>
      <TD align="right">
        <span className="max-md:hidden">Dependent Percentage </span>
        <span className="max-sm:hidden md:hidden">Dependent % </span>
        <span className="sm:hidden">Dep % </span>{" "}
        <InfoPopover
          infoText="The percentage of monthly premiums the business will cover for dependent family members"
          displayComponent={
            <span>
              <Icon className={bem("tooltipIcon")} icon="info-circle" flavor="solid" />
            </span>
          }
          placement="top"
        />
      </TD>
    </>
  );

  const fixedAmountHeaders = () => (
    <>
      <TD align="right">
        <span className="max-lg:hidden">Employee Amount </span>
        <span className="max-md:hidden lg:hidden">Employee Amount </span>
        <span className="max-sm:hidden md:hidden">Employee $ </span>
        <span className="sm:hidden">Emp $ </span>{" "}
        <InfoPopover
          infoText="The maximum monthly amount the business will contribute"
          displayComponent={
            <span>
              <Icon className={bem("tooltipIcon")} icon="info-circle" flavor="solid" />
            </span>
          }
          placement="top"
        />
      </TD>
      <TD align="right">
        <span className="max-md:hidden">Dependent Amount </span>
        <span className="max-sm:hidden md:hidden">Dependent $ </span>
        <span className="sm:hidden">Dep $ </span>{" "}
        <InfoPopover
          infoText="The maximum monthly amount the business will contribute to dependent family members"
          displayComponent={
            <span>
              <Icon className={bem("tooltipIcon")} icon="info-circle" flavor="solid" />
            </span>
          }
          placement="top"
        />
      </TD>
    </>
  );

  const percentAmountQuestions = (name) => (
    <>
      <TD align="right">
        <SIFinalFormFieldNoCol
          name={`${name}.employeePercentage`}
          componentProps={{
            appendContent: "%",
            type: "number",
            min: 0,
            max: 100,
          }}
          component={Input}
          validate={composeValidators(validateRequired, validateZero, validateHundred, validateNumber)}
        />
      </TD>
      <TD align="right">
        <SIFinalFormFieldNoCol
          name={`${name}.dependentPercentage`}
          componentProps={{
            appendContent: "%",
            type: "number",
            min: 0,
            max: 100,
          }}
          component={Input}
          validate={composeValidators(validateRequired, validateZero, validateHundred, validateNumber)}
        />
      </TD>
    </>
  );

  const fixedAmountQuestions = (name) => (
    <>
      <TD align="right">
        <SIFinalFormFieldNoCol
          name={`${name}.employeeFixedAmount`}
          componentProps={{
            prependContent: "$",
            type: "number",
            min: 0,
          }}
          component={Input}
          validate={composeValidators(validateRequired, validateZero, validateNumber)}
        />
      </TD>
      <TD align="right">
        <SIFinalFormFieldNoCol
          name={`${name}.dependentFixedAmount`}
          componentProps={{
            prependContent: "$",
            type: "number",
            min: 0,
          }}
          component={Input}
          validate={composeValidators(validateRequired, validateZero, validateNumber)}
        />
      </TD>
    </>
  );

  return (
    <FieldArray name="tiered_contributions">
      {({ fields }) => (
        <>
          <Row>
            <TypeOfContribution md={4} multipleMedicalPlansOffered={multipleMedicalPlansOffered} />
          </Row>
          <Row mb={3}>
            <Col lg={9}>
              <Table>
                <thead>
                  <SnugTR withBorder={false} highlightFirstCell={false}>
                    <TD pl={0}>Tier</TD>
                    {contributionType === CONTRIBUTION_TYPES.fixed ? fixedAmountHeaders() : percentAmountHeaders()}
                    <TD className={bem("iconHeader")} pr={0} />
                  </SnugTR>
                </thead>
                <tbody>
                  {fields.map((name, index) => (
                    <TR key={index} withBorder={false} style={{ height: "52px" }}>
                      <TD pl={0}>
                        <SIFinalFormFieldNoCol
                          name={`${name}.role`}
                          component={Select}
                          componentProps={{
                            options: tierOptions()[index],
                          }}
                          validate={validateRequired}
                        />
                      </TD>
                      {contributionType === CONTRIBUTION_TYPES.fixed ? fixedAmountQuestions(name) : percentAmountQuestions(name)}
                      <TD pr={0}>
                        {fields.length > MIN_TIERED_CONTRIBUTIONS && (
                          <Button size="xs" color="ghost" circle onClick={() => fields.remove(index)}>
                            <RemoveIcon />
                          </Button>
                        )}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
          <Row>
            <Col>
              {fields.length < MAX_TIERED_CONTRIBUTIONS && (
                <Button color="secondary" size="medium" onClick={() => fields.push({})} mb={4}>
                  Add Tier
                </Button>
              )}
            </Col>
          </Row>
        </>
      )}
    </FieldArray>
  );
};

export default TieredContributionsFormDumb;
